import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import DeliveryPartner from "../../interfaces/DeliveryPartner";
import {
  deleteDeliveryPartnerCommission,
  getAllDeliveryPartners,
  getBusinessUnitOverridesByDeliveryPartner,
  updateDeliveryPartner,
  updateDeliveryPartnerCommission,
  updateDeliveryPartnerOverrides,
  deleteDeliveryPartnerOverride,
  getAllBusinessUnits,
} from "../../utils/api/DeliveryPartnersApi";
import { RootState } from "../store/store";
import { IDLE, LOADING, SUCCEEDED, FAILED, NEEDS_REFRESH } from "./constants";
import BusinessUnit from "../../interfaces/BusinessUnit";
import DeliveryPartnerCommissionRate from "../../interfaces/DeliveryPartnerCommissionRate";
import DeliveryPartnerBusinessUnitOverride from "../../interfaces/DeliveryPartnerBusinessUnitOverride";

export interface DeliveryPartnersState {
  deliveryPartners: DeliveryPartner[];
  deliveryPartnerCommissionRateHistoryList: DeliveryPartnerCommissionRate[];
  deliveryPartnerBusinessUnitOverrides: DeliveryPartnerBusinessUnitOverride[];
  fcrBusinessUnits: string[];
  deliveryPartnerStatus: string;
  businessUnitOverrideStatus: string;
  businessUnitStatus: string;
  deliveryPartnerUpdateStatus: string;
  commissionRateUpdateStatus: string;
  deleteCommissionStatus: string;
  overrideUpdateStatus: string;
  deleteOverrideStatus: string;
  error: any;
  lastUpdated: string;
  message: boolean;
  bulkUploadStatus: string;
}

const initialState: DeliveryPartnersState = {
  deliveryPartners: [],
  deliveryPartnerCommissionRateHistoryList: [],
  deliveryPartnerBusinessUnitOverrides: [],
  fcrBusinessUnits: [],
  deliveryPartnerStatus: IDLE,
  businessUnitOverrideStatus: IDLE,
  businessUnitStatus: IDLE,
  deliveryPartnerUpdateStatus: IDLE,
  commissionRateUpdateStatus: IDLE,
  deleteCommissionStatus: IDLE,
  overrideUpdateStatus: IDLE,
  deleteOverrideStatus: IDLE,
  error: null,
  lastUpdated: "",
  message: false,
  bulkUploadStatus: IDLE,
};

export const fetchDeliveryPartners = createAsyncThunk(
  "/delivery-partners",
  async (param, thunkAPI) => {
    const deliveryPartners: DeliveryPartner[] = await getAllDeliveryPartners();
    return deliveryPartners;
  }
);

export const fetchBusinessUnitOverrides = createAsyncThunk(
  "/delivery-partners/overrides",
  async (deliveryPartnerId, thunkAPI) => {
    const deliveryPartnerBusinessUnitOverrides: DeliveryPartnerBusinessUnitOverride[] =
      await getBusinessUnitOverridesByDeliveryPartner(deliveryPartnerId);
    return deliveryPartnerBusinessUnitOverrides;
  }
);

export const updateDeliveryPartners = createAsyncThunk(
  "/delivery-partners/update",
  async (deliveryPartners, thunkAPI) => {
    const response = await updateDeliveryPartner(deliveryPartners);
    return response;
  }
);

export const updateCommissionRateHistory = createAsyncThunk(
  "/commission-rate-history",
  async (commission, thunkAPI) => {
    const response = await updateDeliveryPartnerCommission(commission);
    return response;
  }
);

export const deleteCommission = createAsyncThunk(
  "/commission-rate-history/id",
  async (commissionId, thunkAPI) => {
    const response = await deleteDeliveryPartnerCommission(commissionId);
    return response;
  }
);

export const updateBusinessUnitOverrides = createAsyncThunk(
  "/overrides",
  async (overrides, thunkAPI) => {
    const response = await updateDeliveryPartnerOverrides(overrides);
    return response;
  }
);

export const deleteBusinessUnitOverride = createAsyncThunk(
  "/overrides/overrideId",
  async (commissionId, thunkAPI) => {
    const response = await deleteDeliveryPartnerOverride(commissionId);
    return response;
  }
);

export const fetchAllBusinessUnitsFCR = createAsyncThunk(
  "/business-units",
  async (param, thunkAPI) => {
    let businessUnitList: BusinessUnit[] = await getAllBusinessUnits();
    let sortedBusinessUnitLocationList = businessUnitList
      .map((bu) => {
        return bu.locationNumber;
      })
      .filter((value, index, self) => {
        return self.indexOf(value) === index;
      })
      .sort();
    return sortedBusinessUnitLocationList;
  }
);

export const deliveryPartnersSlice = createSlice({
  name: "DeliveryPartners",
  initialState,
  reducers: {
    alertRefreshDeliveryPartners: (state) => {
      state.deliveryPartnerStatus = NEEDS_REFRESH;
    },
    alertRefreshUpdateDeliveryPartners: (state, action) => {
      state.deliveryPartnerUpdateStatus = NEEDS_REFRESH;
    },
    alertRefreshGet: (state, action) => {
      state.deliveryPartnerUpdateStatus = NEEDS_REFRESH;
    },
    alertRefreshBusinessUnitOverrides: (state) => {
      state.businessUnitOverrideStatus = NEEDS_REFRESH;
    },
    alertRefreshBusinessUnitFCR: (state) => {
      state.businessUnitStatus = NEEDS_REFRESH;
    },
    updateBusinessUnitOverrides: (state, action) => {
      state.deliveryPartnerBusinessUnitOverrides = action.payload;
    },
    resetBulkUploadStatus: (state) => {
      state.businessUnitOverrideStatus = IDLE;
      state.overrideUpdateStatus = IDLE;
      state.error = null;
    },
    resetUpdateOverridesStatus: (state) => {
      state.overrideUpdateStatus = IDLE;
      state.error = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchDeliveryPartners.pending, (state, action) => {
        state.deliveryPartnerStatus = LOADING;
        state.message = false;
      })
      .addCase(fetchDeliveryPartners.fulfilled, (state, action) => {
        state.deliveryPartnerStatus = SUCCEEDED;
        state.deliveryPartners = action.payload;
        state.message = false;
      })
      .addCase(fetchDeliveryPartners.rejected, (state, action) => {
        state.deliveryPartnerStatus = FAILED;
        state.error = action.error.message;
        state.message = false;
      })
      .addCase(fetchAllBusinessUnitsFCR.pending, (state, action) => {
        state.businessUnitStatus = LOADING;
        state.message = false;
      })
      .addCase(fetchAllBusinessUnitsFCR.fulfilled, (state, action) => {
        state.businessUnitStatus = SUCCEEDED;
        state.fcrBusinessUnits = action.payload;
        state.message = false;
      })
      .addCase(fetchAllBusinessUnitsFCR.rejected, (state, action) => {
        state.businessUnitStatus = FAILED;
        state.error = action.error.message;
        state.message = false;
      })
      .addCase(fetchBusinessUnitOverrides.pending, (state, action) => {
        state.businessUnitOverrideStatus = LOADING;
        state.message = false;
      })
      .addCase(fetchBusinessUnitOverrides.fulfilled, (state, action) => {
        state.businessUnitOverrideStatus = SUCCEEDED;
        state.deliveryPartnerBusinessUnitOverrides = action.payload;
        state.message = false;
      })
      .addCase(fetchBusinessUnitOverrides.rejected, (state, action) => {
        state.businessUnitOverrideStatus = FAILED;
        state.error = action.error.message;
        state.message = false;
      })
      .addCase(updateDeliveryPartners.pending, (state, action) => {
        state.deliveryPartnerUpdateStatus = LOADING;
        state.message = false;
      })
      .addCase(updateDeliveryPartners.fulfilled, (state, action) => {
        state.deliveryPartnerUpdateStatus = SUCCEEDED;
        state.message = true;
      })
      .addCase(updateDeliveryPartners.rejected, (state, action) => {
        state.deliveryPartnerUpdateStatus = FAILED;
        state.error = action.error.message;
        state.message = false;
      })
      .addCase(updateCommissionRateHistory.pending, (state, action) => {
        state.commissionRateUpdateStatus = LOADING;
        state.message = false;
      })
      .addCase(updateCommissionRateHistory.fulfilled, (state, action) => {
        state.commissionRateUpdateStatus = SUCCEEDED;
        state.message = true;
      })
      .addCase(updateCommissionRateHistory.rejected, (state, action) => {
        state.commissionRateUpdateStatus = FAILED;
        state.error = action.error.message;
        state.message = false;
      })
      .addCase(deleteCommission.pending, (state, action) => {
        state.deleteCommissionStatus = LOADING;
        state.message = false;
      })
      .addCase(deleteCommission.fulfilled, (state, action) => {
        state.deleteCommissionStatus = SUCCEEDED;
        state.message = true;
      })
      .addCase(deleteCommission.rejected, (state, action) => {
        state.deleteCommissionStatus = FAILED;
        state.error = action.error.message;
        state.message = false;
      })
      .addCase(updateBusinessUnitOverrides.pending, (state, action) => {
        state.overrideUpdateStatus = LOADING;
        state.message = false;
      })
      .addCase(updateBusinessUnitOverrides.fulfilled, (state, action) => {
        state.overrideUpdateStatus = SUCCEEDED;
        state.message = true;
      })
      .addCase(updateBusinessUnitOverrides.rejected, (state, action) => {
        state.overrideUpdateStatus = FAILED;
        state.error = action.error.message;
        state.message = false;
      })
      .addCase(deleteBusinessUnitOverride.pending, (state, action) => {
        state.deleteOverrideStatus = LOADING;
        state.message = false;
      })
      .addCase(deleteBusinessUnitOverride.fulfilled, (state, action) => {
        state.deleteOverrideStatus = SUCCEEDED;
        state.message = true;
      })
      .addCase(deleteBusinessUnitOverride.rejected, (state, action) => {
        state.deleteOverrideStatus = FAILED;
        state.error = action.error.message;
        state.message = false;
      });
  },
});

export default deliveryPartnersSlice.reducer;
export const {
  alertRefreshDeliveryPartners,
  resetBulkUploadStatus,
  resetUpdateOverridesStatus,
} = deliveryPartnersSlice.actions;
export const deliveryPartnerSelector = (state: RootState) =>
  state.deliveryPartners.deliveryPartners;

export const FcrBusinessUnitSelector = (state: RootState) =>
  state.fcrBusinessUnits.fcrBusinessUnits;

export const deliveryPartnerCommissionRateHistorySelector = (
  state: RootState
) => state.deliveryPartners.deliveryPartnerCommissionRateHistoryList;

export const deliveryPartnerBusinessUnitOverrideSelector = (state: RootState) =>
  state.deliveryPartners.deliveryPartnerBusinessUnitOverrides;

export const deliveryPartnerBusinessUnitUpdateSelector = (state: RootState) =>
  state.message;
